// statistics_clinic_05_practice_probability_empirical_rule.md — 3 DIY cases
// covering all three 68-95-99.7 rule bands: within 1 SD (68%), within 2 SD /
// defect rate (95%/5%), and a one-sided beyond-3-SD tail (0.15%).

export const STATS_CLINIC_05_TRACKER_ID = 'statistics-clinic-05-practice-bank';

export const STATS_CLINIC_05_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_05_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Manufacturing tolerance check',
    scenario: "A quality manager is setting the acceptable tolerance band for a machined part's diameter and wants to know what proportion of parts will pass, and what proportion will be scrapped as defective, if the spec is set at plus or minus two standard deviations from the target.",
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Mean diameter', '50.0 mm'], ['Standard deviation', '0.2 mm'], ['Spec range', '49.6 mm to 50.4 mm']],
    }],
    answerGuide: {
      business_question: 'What proportion of parts pass the spec, and what proportion are defective?',
      statistic_family: 'Probability / empirical rule.',
      statistic_chosen: '68-95-99.7 rule, within 2 SD.',
      why_statistic: 'The spec range (49.6 to 50.4mm) is exactly mean minus 2 SD to mean plus 2 SD (50.0 - 0.4 to 50.0 + 0.4).',
      calculation: 'Within 2 SD = 50.0 - (2x0.2) to 50.0 + (2x0.2) = 49.6 to 50.4mm, which matches the spec range exactly. Defect rate = 100% - 95% = 5%.',
      result: 'About 95% of parts pass the spec; about 5% are defective.',
      meaning: '1 in 20 parts, on average, will fall outside the tolerance band and be scrapped.',
      decision: 'Report the expected 5% scrap rate to production planning so material and cost budgets account for it.',
      caution: 'This only holds if part diameter is genuinely close to normally distributed — check for a systematic machine drift before trusting the 5% figure.',
    },
    ruleTerms: {
      business_question: ['proportion', 'pass', 'defective'],
      statistic_family: ['probability', 'empirical rule'],
      statistic_chosen: ['2 sd', 'within 2'],
      why_statistic: ['49.6', '50.4', '2 sd'],
      calculation: ['95%', '5%'],
      result: ['95%', '5%'],
      meaning: ['1 in 20', 'scrapped'],
      decision: ['5%', 'scrap rate', 'planning'],
      caution: ['normally distributed', 'drift'],
    },
    criticalTerms: { result: ['95%', '5%'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Productive hours planning',
    scenario: 'An operations manager is writing a staffing-capacity note and wants to describe the "normal" range of daily productive hours logged by the team, without listing every individual employee\'s figure.',
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Mean productive hours', '6.5 hours'], ['Standard deviation', '0.5 hours']],
    }],
    answerGuide: {
      business_question: 'What is the normal range of daily productive hours for the team?',
      statistic_family: 'Probability / empirical rule.',
      statistic_chosen: '68-95-99.7 rule, within 1 SD.',
      why_statistic: '"Normal" maps to the middle 68% band of a roughly normal distribution — the most common everyday range.',
      calculation: 'Within 1 SD = 6.5 - 0.5 to 6.5 + 0.5 = 6.0 to 7.0 hours.',
      result: 'About 68% of days fall between 6.0 and 7.0 productive hours.',
      meaning: 'Most days sit in a fairly tight 6-7 hour band; the remaining 32% splits into lower- and higher-than-usual days.',
      decision: 'Use "6-7 hours" as the normal daily range in the capacity note, rather than the single mean figure alone.',
      caution: 'Confirm productive-hours logging is roughly symmetric before applying the rule — a hard cap (e.g. an 8-hour shift limit) could skew the real distribution.',
    },
    ruleTerms: {
      business_question: ['normal range', 'productive hours'],
      statistic_family: ['probability', 'empirical rule'],
      statistic_chosen: ['1 sd', 'within 1'],
      why_statistic: ['normal', 'middle', '68'],
      calculation: ['6.0', '7.0'],
      result: ['68%', '6.0', '7.0'],
      meaning: ['tight', 'band'],
      decision: ['6-7 hours', 'capacity note'],
      caution: ['symmetric', 'cap', 'skew'],
    },
    criticalTerms: { result: ['68%'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Server response-time alerting',
    scenario: 'A platform engineer is setting an automatic alert threshold for API response times and wants to know how rare a response above 150ms would genuinely be, before deciding whether that threshold will trigger too many false alarms.',
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Mean response time', '120 ms'], ['Standard deviation', '10 ms'], ['Proposed alert threshold', 'above 150 ms']],
    }],
    answerGuide: {
      business_question: 'How rare is a response time above 150ms, and is that a sensible alert threshold?',
      statistic_family: 'Probability / empirical rule.',
      statistic_chosen: '68-95-99.7 rule, beyond 3 SD (one tail).',
      why_statistic: '150ms is exactly mean + 3 SD (120 + 3x10); the question asks about one tail (slow responses only), not both tails.',
      calculation: 'mean + 3 SD = 120 + 30 = 150ms. % outside 3 SD (both tails) = 100% - 99.7% = 0.3%. Split evenly (symmetric curve): 0.3%/2 = 0.15% in the slow-response tail alone.',
      result: 'About 0.15% of responses are expected to exceed 150ms.',
      meaning: 'A response above 150ms is genuinely rare under normal conditions, not routine variation.',
      decision: '150ms is a sensible alert threshold — it will trigger rarely under normal load, so an alert firing is a meaningful signal worth investigating.',
      caution: 'Do not use the 0.3% two-tailed figure for a one-sided question — that would overstate how often slow responses actually occur by 2x.',
    },
    ruleTerms: {
      business_question: ['rare', '150ms', 'alert threshold'],
      statistic_family: ['probability', 'empirical rule'],
      statistic_chosen: ['3 sd', 'beyond 3', 'tail'],
      why_statistic: ['mean + 3', 'one tail'],
      calculation: ['150', '0.3%', '0.15%'],
      result: ['0.15%'],
      meaning: ['rare', 'not routine'],
      decision: ['sensible', 'alert threshold'],
      caution: ['0.3%', 'one-sided', 'overstate'],
    },
    criticalTerms: { calculation: ['0.15%'], result: ['0.15%'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Daily call-volume capacity',
    scenario: 'A contact centre says daily call volume is roughly normal. The workforce planner wants a staffing range that covers about 95% of ordinary days.',
    tables: [{
      columns: ['metric', 'value'],
      rows: [['Mean daily calls', '800'], ['Standard deviation', '60'], ['Target coverage', '95% of ordinary days']],
    }],
    answerGuide: {
      business_question: 'What call-volume range covers about 95% of ordinary days?',
      statistic_family: 'Probability / empirical rule.',
      statistic_chosen: '68-95-99.7 rule, within 2 SD.',
      why_statistic: 'The target is a central 95% operating range for roughly normal data, which maps to mean +/- 2 SD.',
      calculation: 'Lower bound = 800 - (2x60) = 680. Upper bound = 800 + (2x60) = 920.',
      result: 'About 95% of days are expected to fall between 680 and 920 calls.',
      meaning: '920 calls is a sensible upper planning level for ordinary variation, not a guarantee that higher-volume days never happen.',
      decision: 'Plan normal staffing capacity around the 680-920 range, with an overflow plan for days above 920.',
      caution: 'Do not pool unlike days if there is seasonality; calculate separate means and SDs for comparable days.',
    },
    ruleTerms: {
      business_question: ['range', '95%', 'ordinary days'],
      statistic_family: ['probability', 'empirical rule'],
      statistic_chosen: ['2 sd', 'within 2'],
      why_statistic: ['95%', 'normal', '2 sd'],
      calculation: ['680', '920'],
      result: ['680', '920', '95%'],
      meaning: ['upper planning', 'ordinary'],
      decision: ['staffing', 'overflow'],
      caution: ['seasonality', 'comparable'],
    },
    criticalTerms: { calculation: ['680', '920'], result: ['95%'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Basket-to-purchase conversion',
    scenario: 'An ecommerce analyst is checking funnel quality. 32% of visitors add an item to basket. 14% of all visitors both add to basket and complete a purchase. The product manager asks: "What percentage of basket-adders purchase?"',
    tables: [{
      columns: ['event', 'probability'],
      rows: [['Add to basket', '32%'], ['Add to basket and purchase', '14%']],
    }],
    answerGuide: {
      business_question: 'What percentage of basket-adders complete a purchase?',
      statistic_family: 'Basic probability.',
      statistic_chosen: 'Conditional probability.',
      why_statistic: 'The question asks for purchase given that the visitor added to basket, so the denominator is basket-adders, not all visitors.',
      calculation: 'P(purchase | basket) = P(purchase and basket) / P(basket) = 0.14 / 0.32 = 0.4375.',
      result: '43.75% of basket-adders purchase.',
      meaning: 'Among visitors who add to basket, a little under half complete the transaction.',
      decision: 'Report basket-to-purchase conversion as 43.75%, then investigate checkout friction for the remaining 56.25%.',
      caution: 'Do not report 14% as the basket conversion rate; 14% is measured across all visitors, not only basket-adders.',
    },
    ruleTerms: {
      business_question: ['basket', 'purchase'],
      statistic_family: ['probability'],
      statistic_chosen: ['conditional'],
      why_statistic: ['given', 'denominator'],
      calculation: ['0.14', '0.32', '0.4375'],
      result: ['43.75%'],
      meaning: ['basket-adders', 'under half'],
      decision: ['43.75%', 'checkout'],
      caution: ['14%', 'all visitors'],
    },
    criticalTerms: { calculation: ['0.4375'], result: ['43.75%'] },
  },
];

export const STATS_CLINIC_05_CONFIG = {
  trackerId: STATS_CLINIC_05_TRACKER_ID,
  resourceKey: 'statistics_clinic_05_practice_probability_empirical_rule',
  rubricKey: 'statistics_probability_empirical_rule',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Probability & Empirical Rule Cases',
  description: 'Each case asks you to choose the right probability route: empirical-rule range, one-sided tail, overlap rule, or conditional conversion. State the denominator and the range before giving the percentage. Use the worked solution guide after trying the case, then save your own answer.',
  introAlert: 'Read the wording first. "Within" is a range, "longer than" is one tail, "or" may need overlap removed, and "given" means conditional probability. Open the worked solution if you are stuck on the route or calculation.',
  decisionFramework: {
    title: 'Flagged decision framework: probability findings',
    description: 'Do not stop at the percentage. Convert the probability into an action: ordinary range, review flag, escalation, or denominator correction.',
    rules: [
      'Within 1 SD: describe the normal everyday band; do not call values outside it errors.',
      'Within 2 SD: use as a planning/tolerance band; expect about 5% outside it if the process is stable.',
      'Beyond 3 SD in one tail: treat as rare and worth review; use 0.15%, not 0.3%, for one-sided tail questions.',
      'Conditional probability: name the denominator explicitly, otherwise the business may act on the wrong conversion rate.',
    ],
    responseFrame: 'Frame the response as: "This finding is [ordinary / unusual / rare / denominator-dependent]. The calculation is [route and value]. Therefore I would [plan / flag / escalate / correct the denominator], with the caution that [normality, seasonality, or denominator risk] must be checked."',
  },
  lenses: STATS_CLINIC_05_LENSES,
  workingField: null,
  items: STATS_CLINIC_05_ITEMS,
};
